/**
 * useSessionTimeout — Client session timeout management with inactivity detection
 * Tracks user activity and shows warning modal before auto-logout.
 *
 * Usage:
 *   const { showWarning, remainingSeconds, dismissWarning } = useSessionTimeout(
 *     60 * 60 * 1000,  // timeoutMs: 1 hour
 *     5 * 60 * 1000    // warningMs: 5 minutes before timeout
 *   );
 */
import { useState, useEffect, useCallback, useRef } from "react";

interface UseSessionTimeoutOptions {
  timeoutMs?: number; // Total inactivity timeout in milliseconds (default: 1 hour)
  warningMs?: number; // Time before timeout to show warning (default: 5 minutes)
}

interface UseSessionTimeoutReturn {
  showWarning: boolean;
  remainingSeconds: number;
  dismissWarning: () => void;
}

const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"] as const;
const CHECK_INTERVAL_MS = 30_000; // Check every 30 seconds (not every second)

export function useSessionTimeout(
  timeoutMs: number = 60 * 60 * 1000, // 1 hour default
  warningMs: number = 5 * 60 * 1000 // 5 minutes warning default
): UseSessionTimeoutReturn {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const lastActivityRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Reset the inactivity timer
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setRemainingSeconds(0);
  }, []);

  // Dismiss warning and reset timer (called by "Continuar" button)
  const dismissWarning = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  // Check activity status every CHECK_INTERVAL_MS
  useEffect(() => {
    const checkActivity = () => {
      const now = Date.now();
      const elapsedMs = now - lastActivityRef.current;
      const warningThresholdMs = timeoutMs - warningMs;

      // If we've exceeded warning threshold, show warning and calculate remaining time
      if (elapsedMs >= warningThresholdMs) {
        const remainingMs = timeoutMs - elapsedMs;

        if (remainingMs <= 0) {
          // Session has timed out — trigger logout
          setShowWarning(false);
          // Call logout via supabase (will be handled by parent component)
          window.dispatchEvent(
            new CustomEvent("session-expired", {
              detail: { reason: "inactivity" },
            })
          );
        } else {
          // Show warning and countdown
          setShowWarning(true);
          setRemainingSeconds(Math.ceil(remainingMs / 1000));
        }
      } else {
        // Still within safe zone
        setShowWarning(false);
        setRemainingSeconds(0);
      }
    };

    // Start checking immediately
    checkActivity();

    // Check every 30 seconds
    checkIntervalRef.current = setInterval(checkActivity, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [timeoutMs, warningMs]);

  // Register activity listeners
  useEffect(() => {
    const handleActivity = () => {
      // Only reset if we're not already in warning state
      if (!showWarning) {
        resetActivity();
      }
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity);
      }
    };
  }, [showWarning, resetActivity]);

  return { showWarning, remainingSeconds, dismissWarning };
}

export default useSessionTimeout;
